import { useMemo } from 'react';
import { useFormikContext } from 'formik';
import { Col, Radio, Row, Space } from 'antd';
import { usePartyContext } from 'views/customers/customer/PartyProvider';
import useSession from 'hooks/useSession';
import { useRequestPromise } from 'hooks/useRequest';
import { getCustomer } from 'store/customers/client';
import { DutyPayer } from 'store/customers/customer';
import VatTreatmentSelect from 'views/customers/components/create-customer-drawer/VatTreatmentSelect';
import DutyPayerSelect, {
    getDutyPayerOptions,
} from 'views/customers/components/create-customer-drawer/DutyPayerSelect';
import AEP5AuthorisationSection from 'views/customers/components/create-customer-drawer/AEP5AuthorisationSection';
import { FormFields, FormValues } from '../../types';
import { FieldLabel, FormCol, FormRow } from '../../Utils';

const DutyAndVatForm = () => {
    const formik = useFormikContext<FormValues>();
    const { customer } = usePartyContext();
    const { customerId: tenantId } = useSession();

    const { data: tenant } = useRequestPromise(() => (tenantId ? getCustomer(tenantId) : null), undefined, [tenantId]);

    const dutyPayerOptions = useMemo(
        () => getDutyPayerOptions(tenant?.eori, formik.values.eori || customer?.eori),
        [tenant?.eori, formik.values.eori, customer?.eori]
    );

    const dutyPayerIsTheConsignee = formik.values?.dutyPayer === DutyPayer.CONSIGNEE;

    return (
        <Space size={12} direction="vertical" style={{ width: '100%', marginTop: 16 }}>
            <FormRow>
                <FormCol>
                    <FieldLabel htmlFor={FormFields.DutyAndVat.DUTY_PAYER} required>
                        Duty Payer
                    </FieldLabel>
                    <div>
                        <DutyPayerSelect
                            condensed
                            options={dutyPayerOptions}
                            fieldProps={formik.getFieldProps(FormFields.DutyAndVat.DUTY_PAYER)}
                            fieldHelper={formik.getFieldHelpers(FormFields.DutyAndVat.DUTY_PAYER)}
                            fieldMeta={formik.getFieldMeta(FormFields.DutyAndVat.DUTY_PAYER)}
                        />
                    </div>
                </FormCol>
                <FormCol>
                    <FieldLabel htmlFor={FormFields.DutyAndVat.VAT_TREATMENT} required>
                        VAT Treatment
                    </FieldLabel>
                    <div>
                        <VatTreatmentSelect
                            condensed
                            fieldProps={formik.getFieldProps(FormFields.DutyAndVat.VAT_TREATMENT)}
                            fieldHelper={formik.getFieldHelpers(FormFields.DutyAndVat.VAT_TREATMENT)}
                            fieldMeta={formik.getFieldMeta(FormFields.DutyAndVat.VAT_TREATMENT)}
                        />
                    </div>
                </FormCol>
            </FormRow>

            <FormRow>
                {dutyPayerIsTheConsignee && (
                    <FormCol>
                        <AEP5AuthorisationSection
                            checked={formik.values[FormFields.DutyAndVat.HAS_AEP5_AUTHORIZATION] ?? false}
                            onChange={(e) =>
                                formik.setFieldValue(FormFields.DutyAndVat.HAS_AEP5_AUTHORIZATION, e.target.checked)
                            }
                        />
                    </FormCol>
                )}

                <FormCol>
                    <FieldLabel htmlFor={FormFields.DutyAndVat.VAT_EXEMPTION}>VAT Exemption</FieldLabel>
                    <Radio.Group
                        style={{ width: '100%' }}
                        value={formik.values[FormFields.DutyAndVat.VAT_EXEMPTION]}
                        onChange={(e) => formik.setFieldValue(FormFields.DutyAndVat.VAT_EXEMPTION, e.target.value)}
                    >
                        <Row>
                            <Col span={12}>
                                <Radio value={true}>Yes</Radio>
                            </Col>
                            <Col span={12}>
                                <Radio value={false}>No</Radio>
                            </Col>
                        </Row>
                    </Radio.Group>
                </FormCol>
            </FormRow>
        </Space>
    );
};

export default DutyAndVatForm;
